// Pure easing for the JobDetailPanel open/close animation.
//
// The hook used to inline the (rotY, opacity)-as-a-function-of-t math
// inside two GSAP onUpdate closures. Pulling it out as pure functions
// makes the animation curve testable without driving a real tween.

export interface OpenTransform {
  /** degrees — fully open at 0, fully closed at ROT_MAX. */
  readonly rotY: number;
  /** 0..1 — partial during the open transition. */
  readonly opacity: number;
}

/** Matches the value used in the hook — kept in sync via re-export. */
export const ROT_MAX = 55;

/**
 * Open-transition curve.
 * - t in [0, 0.5]: rotY interpolates ROT_MAX → 0, opacity 0.3 → 1
 *   (the 3D fold completes by the midpoint).
 * - t in [0.5, 1]: rotY stays 0, opacity stays 1 (the width tween
 *   takes over after the fold has finished).
 *
 * Input t clamps to [0, 1] so callers don't have to.
 */
export const computeOpenTransform = (t: number): OpenTransform => {
  const u = t <= 0 ? 0 : t >= 1 ? 1 : t;
  const rotT = u * 2 >= 1 ? 1 : u * 2;
  return {
    rotY: ROT_MAX * (1 - rotT),
    opacity: 0.3 + 0.7 * rotT,
  };
};

/**
 * Close-transition curve. Mirrors `computeOpenTransform` along the
 * midpoint: the 3D fold doesn't start until t crosses 0.5, then
 * accelerates to ROT_MAX while opacity decays 1 → 0.3.
 */
export const computeCloseTransform = (t: number): OpenTransform => {
  const u = t <= 0 ? 0 : t >= 1 ? 1 : t;
  const rotT = (u - 0.5) * 2;
  const rotTc = rotT <= 0 ? 0 : rotT >= 1 ? 1 : rotT;
  return {
    rotY: ROT_MAX * rotTc,
    opacity: 1 - 0.7 * rotTc,
  };
};
